import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Snackbar, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { walletApi } from '../../../services/api/walletApi';
import '../accountTheme.css';
import './AddBankAccountScreen.css';

const LIST_PICKER_NONE = '';

function AddBankAccountScreen({ onBack, onSave }) {
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [listPicker, setListPicker] = useState(LIST_PICKER_NONE);
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [ifscHint, setIfscHint] = useState('');
  const [ifscLoading, setIfscLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    walletApi
      .banks()
      .then((data) => setBanks(data.banks ?? []))
      .catch(() => setToast('Could not load bank list — type your bank name below'))
      .finally(() => setLoadingBanks(false));
  }, []);

  useEffect(() => {
    const code = ifscCode.trim().toUpperCase();
    if (code.length !== 11) {
      setIfscHint('');
      return undefined;
    }

    const timer = setTimeout(async () => {
      setIfscLoading(true);
      try {
        const data = await walletApi.lookupIfsc(code);
        setIfscHint(`${data.branch}, ${data.city}`);
        if (data.bankName) {
          const match = banks.find(
            (b) => b.toLowerCase() === data.bankName.toLowerCase(),
          );
          const resolved = match || data.bankName;
          setBankName(resolved);
          setListPicker(match || LIST_PICKER_NONE);
        }
      } catch (err) {
        setIfscHint(err.message || 'IFSC not found');
      } finally {
        setIfscLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [ifscCode, banks]);

  const trimmedBankName = bankName.trim();

  const canSave = useMemo(
    () =>
      trimmedBankName.length >= 2
      && accountHolderName.trim().length >= 2
      && accountNumber.trim().length >= 8
      && phoneNumber.trim().length === 10
      && ifscCode.trim().length === 11,
    [trimmedBankName, accountHolderName, accountNumber, phoneNumber, ifscCode],
  );

  const handleListPick = (value) => {
    setListPicker(value);
    if (value) setBankName(value);
  };

  const handleBankNameChange = (value) => {
    setBankName(value);
    const match = banks.find((b) => b.toLowerCase() === value.trim().toLowerCase());
    setListPicker(match || LIST_PICKER_NONE);
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setToast('');
    try {
      const result = await walletApi.addBankAccount({
        bankName: trimmedBankName,
        accountHolderName: accountHolderName.trim(),
        accountNumber: accountNumber.trim(),
        phoneNumber: phoneNumber.trim(),
        ifscCode: ifscCode.trim().toUpperCase(),
      });
      onSave?.(result.account);
    } catch (err) {
      setToast(err.message || 'Could not save bank account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="bz-page">
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />
      <Box className="bz-topbar">
        <IconButton size="small" onClick={onBack} className="bz-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="bz-topbar__title">Add bank</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box className="bz-scroll">
        <Box className="bz-card add-bank-form">
          {!loadingBanks && banks.length > 0 && (
            <>
              <label className="add-bank-label" htmlFor="bank-list-picker">
                Pick from list (optional)
              </label>
              <select
                id="bank-list-picker"
                className="add-bank-select"
                value={listPicker}
                onChange={(e) => handleListPick(e.target.value)}
              >
                <option value={LIST_PICKER_NONE}>— Select a bank —</option>
                {banks.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </>
          )}

          {loadingBanks ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
                Loading banks…
              </Typography>
            </Box>
          ) : null}

          <label className="add-bank-label" htmlFor="bank-name-input">
            Bank name
          </label>
          <input
            id="bank-name-input"
            className="add-bank-input"
            placeholder="Type or select your bank name"
            value={bankName}
            onChange={(e) => handleBankNameChange(e.target.value)}
            autoComplete="organization"
            list={banks.length > 0 ? 'bank-name-suggestions' : undefined}
          />
          {banks.length > 0 && (
            <datalist id="bank-name-suggestions">
              {banks.map((b) => (
                <option key={b} value={b} />
              ))}
            </datalist>
          )}

          <label className="add-bank-label" htmlFor="ifsc-input">IFSC code</label>
          <input
            id="ifsc-input"
            className="add-bank-input"
            placeholder="e.g. SBIN0001234"
            value={ifscCode}
            onChange={(e) => setIfscCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11))}
          />
          {ifscLoading ? (
            <Typography className="add-bank-hint">Verifying IFSC…</Typography>
          ) : ifscHint ? (
            <Typography className="add-bank-hint">{ifscHint}</Typography>
          ) : null}

          <label className="add-bank-label" htmlFor="holder-input">Account holder name</label>
          <input
            id="holder-input"
            className="add-bank-input"
            placeholder="Full name as per bank"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />

          <label className="add-bank-label" htmlFor="account-input">Account number</label>
          <input
            id="account-input"
            className="add-bank-input"
            placeholder="Enter account number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 18))}
            inputMode="numeric"
          />

          <label className="add-bank-label" htmlFor="phone-input">Phone number</label>
          <input
            id="phone-input"
            className="add-bank-input"
            placeholder="10-digit mobile"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
            inputMode="numeric"
          />

          {!canSave && !saving && (
            <Typography className="add-bank-hint add-bank-hint--muted">
              Fill bank name, 11-character IFSC, holder name, account number (8+ digits), and 10-digit phone to save.
            </Typography>
          )}
        </Box>
      </Box>

      <Box className="bz-bottom-bar">
        <button type="button" className="bz-primary-btn" disabled={!canSave || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save bank account'}
        </button>
      </Box>
    </Box>
  );
}

export default AddBankAccountScreen;
